import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Megaphone, Upload, Pencil, Eye, EyeOff, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaSrc = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND}/api/files/${url}`);
const MEDIA_TYPES = [
  { key: "image", label: "Image / Affiche" },
  { key: "video", label: "Vidéo (fichier)" },
  { key: "youtube", label: "Vidéo YouTube" },
];
const empty = { title: "", description: "", media_type: "image", media_url: "", link_url: "", cta_label: "En savoir plus", active: true, order: 0 };

export function AdsManager() {
  const [ads, setAds] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // ad id or null
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/ads"); setAds(data); }
    catch (e) { toast.error(apiError(e, "Erreur de chargement des publicités")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (a) => {
    setForm({ title: a.title, description: a.description || "", media_type: a.media_type || "image",
      media_url: a.media_url || "", link_url: a.link_url || "", cta_label: a.cta_label || "En savoir plus",
      active: a.active !== false, order: a.order || 0 });
    setEditing(a.id); setOpen(true);
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/ad", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, media_url: data.path }));
      toast.success("Média téléversé");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Un titre est requis"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/admin/ads/${editing}`, form);
      else await api.post("/admin/ads", form);
      toast.success(editing ? "Publicité mise à jour" : "Publicité créée");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try { await api.patch(`/admin/ads/${a.id}`, { ...a, active: !a.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette publicité ?")) return;
    try { await api.delete(`/admin/ads/${id}`); toast.success("Publicité supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!ads) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;

  return (
    <div data-testid="ads-manager">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone size={18} className="text-ciel" />
          <div>
            <h3 className="font-display text-base font-semibold text-navy">Plages publicitaires</h3>
            <p className="text-xs text-slate-500">Encarts affichés sur la page d'accueil. Les emplacements libres sont remplis automatiquement par les prochaines sessions publiques.</p>
          </div>
        </div>
        <Button data-testid="ad-new-btn" onClick={openNew} className="rounded-full bg-ciel text-white hover:bg-sky-700"><Plus size={15} className="mr-1" /> Nouvel encart</Button>
      </div>

      {ads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400" data-testid="ads-empty">
          Aucun encart manuel. La page d'accueil affiche automatiquement les prochaines sessions publiques (webinaires / formations).
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ads.map((a) => (
            <div key={a.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`ad-row-${a.id}`}>
              <div className="relative aspect-[16/9] bg-slate-100">
                {a.media_type === "image" && a.media_url && <img src={mediaSrc(a.media_url)} alt={a.title} className="h-full w-full object-cover" />}
                {a.media_type === "video" && a.media_url && <video src={mediaSrc(a.media_url)} className="h-full w-full object-cover" muted />}
                {a.media_type === "youtube" && <div className="flex h-full items-center justify-center text-xs text-slate-400">Vidéo YouTube</div>}
                {!a.media_url && a.media_type !== "youtube" && <div className="flex h-full items-center justify-center text-xs text-slate-300">Sans média</div>}
                <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.active ? "bg-emerald-500 text-white" : "bg-slate-300 text-slate-700"}`}>{a.active ? "Actif" : "Masqué"}</span>
              </div>
              <div className="p-4">
                <p className="font-display text-sm font-semibold text-navy">{a.title}</p>
                {a.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{a.description}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => openEdit(a)} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:text-ciel" data-testid={`ad-edit-${a.id}`}><Pencil size={14} /></button>
                  <button onClick={() => toggleActive(a)} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:text-ciel" data-testid={`ad-toggle-${a.id}`}>{a.active ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  <button onClick={() => del(a.id)} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:text-red-500" data-testid={`ad-del-${a.id}`}><Trash2 size={14} /></button>
                  <span className="ml-auto flex items-center gap-3 font-mono text-[11px] text-slate-500" data-testid={`ad-stats-${a.id}`}>
                    <span className="flex items-center gap-1" title="Vues"><Eye size={12} /> {a.views || 0}</span>
                    <span className="flex items-center gap-1" title="Clics"><MousePointerClick size={12} /> {a.clicks || 0}</span>
                    <span title="Taux de clics">{a.views ? Math.round(((a.clicks || 0) / a.views) * 100) : 0}%</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="ad-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{editing ? "Modifier l'encart" : "Nouvel encart publicitaire"}</DialogTitle>
            <DialogDescription>Image ou vidéo (taille d'une fiche produit), titre, texte et bouton d'action.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Titre</Label><Input data-testid="ad-title" value={form.title} className="mt-1.5" onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Description</Label><Textarea data-testid="ad-desc" value={form.description} className="mt-1.5" onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Type de média</Label>
                <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v, media_url: "" })}>
                  <SelectTrigger className="mt-1.5" data-testid="ad-media-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEDIA_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Ordre d'affichage</Label><Input type="number" data-testid="ad-order" value={form.order} className="mt-1.5 font-mono" onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            </div>

            {form.media_type === "youtube" ? (
              <div><Label className="text-xs text-slate-500">Lien YouTube</Label><Input data-testid="ad-youtube-url" placeholder="https://youtube.com/watch?v=…" value={form.media_url} className="mt-1.5" onChange={(e) => setForm({ ...form, media_url: e.target.value })} /></div>
            ) : (
              <div>
                <Label className="text-xs text-slate-500">{form.media_type === "video" ? "Vidéo (MP4)" : "Image / affiche"}</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:border-ciel" data-testid="ad-upload-label">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Téléverser
                    <input type="file" accept={form.media_type === "video" ? "video/*" : "image/*"} className="hidden" data-testid="ad-upload-input" onChange={(e) => uploadMedia(e.target.files?.[0])} />
                  </label>
                  {form.media_url && (form.media_type === "image"
                    ? <img src={mediaSrc(form.media_url)} alt="aperçu" className="h-12 w-20 rounded object-cover" />
                    : <video src={mediaSrc(form.media_url)} className="h-12 w-20 rounded object-cover" muted />)}
                </div>
                <Input placeholder="ou collez une URL d'image publique" value={form.media_url.startsWith("http") ? form.media_url : ""} className="mt-2 text-xs" onChange={(e) => setForm({ ...form, media_url: e.target.value })} data-testid="ad-image-url" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Lien du bouton</Label><Input data-testid="ad-link" placeholder="/catalogue ou https://…" value={form.link_url} className="mt-1.5" onChange={(e) => setForm({ ...form, link_url: e.target.value })} /></div>
              <div><Label className="text-xs text-slate-500">Texte du bouton</Label><Input data-testid="ad-cta" value={form.cta_label} className="mt-1.5" onChange={(e) => setForm({ ...form, cta_label: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <Switch data-testid="ad-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Encart actif (visible sur le site)
            </label>
            <Button data-testid="ad-save" onClick={save} disabled={busy || uploading} className="w-full rounded-full bg-ciel text-white hover:bg-sky-700">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
